import { format, isEqual } from 'date-fns';
import { Image } from 'expo-image';
import {
  Stack,
  useFocusEffect,
  useLocalSearchParams,
  useRouter,
} from 'expo-router';
import { t } from 'i18next';
import { DateTime } from 'luxon';
import React from 'react';
import { Dimensions, Platform, Pressable, View } from 'react-native';
import { ScrollView } from 'react-native-gesture-handler';
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view';
import Modal from 'react-native-modal';
import { useSharedValue } from 'react-native-reanimated';
import type { ICarouselInstance } from 'react-native-reanimated-carousel';
import Carousel, { Pagination } from 'react-native-reanimated-carousel';
import { createStyleSheet, useStyles } from 'react-native-unistyles';

import type { Car } from '@/api/cars/types';
import { useCar } from '@/api/cars/use-car';
import { useCarAvailable } from '@/api/cars/use-car-available';
import { useCustomer } from '@/api/customers/use-customer';
import type {
  CancellationPolicy,
  Discount as DiscountType,
  Insurance,
} from '@/api/reservations/types';
import { useAddOns } from '@/api/reservations/use-add-ons';
import type { Address } from '@/api/subscriptions/types';
import BackButton from '@/components/back-button';
import CompareCancellationPoliciesModal from '@/components/booking/compare-cancellation-policies-modal';
import { FinishProfileModal } from '@/components/checkout/finish-profile-modal';
import { DeliveryFeeModal } from '@/components/delivery-fee-modal';
import { MapView } from '@/components/map-view';
import {
  NavigationButton,
  NavigationButtonTypes,
} from '@/components/navigation-button';
import type { CarMarkerProps } from '@/components/search/car-marker';
import {
  formatNumber,
  getCarBrandLogo,
  getCarContentData,
  getCarImages,
  getColorImgSrc,
  getDailyInsuranceCost,
  isDesktop,
  isInsuranceFree,
  midpoint,
  openAddressInMaps,
  useAuth,
  useCurrentOrganization,
} from '@/core';
import mixpanelService from '@/core/clients/mixpanel';
import { useCarDetailTracking } from '@/core/hooks/use-car-detail-tracking';
import useGoogleMapsApi from '@/core/hooks/use-google-maps-api';
import { useTrackMetaEvent } from '@/core/hooks/use-track-meta-event';
import useBookCarStore from '@/stores/use-book-car';
import useSearchStore from '@/stores/use-search-store';
import { PINKLING } from '@/style/colors';
import { Button, Card, showErrorMessage, Text } from '@/ui';
import { CardItem } from '@/ui/card-item';
import { CancellationPolicyCardItem } from '@/ui/card-item/cancellation-policy-card-item';
import { InsuranceCardItem } from '@/ui/card-item/insurance-card-item';
import { CheckItem } from '@/ui/check-item';
import { Chip } from '@/ui/chip';
import Collapsible from '@/ui/collapsible';
import { ConfirmModal } from '@/ui/confirm-modal';
import { Discount } from '@/ui/discount';
import GoogleAutocomplete from '@/ui/google-autocomplete';
import { Icon } from '@/ui/icon';
import { Car as CarIcon, Info } from '@/ui/icons';
import { Battery } from '@/ui/icons/battery';
import { Cancel } from '@/ui/icons/cancel';
import { ChevronLeft } from '@/ui/icons/chevron-left';
import { ChevronRight } from '@/ui/icons/chevron-right';
import { Exclamations } from '@/ui/icons/exclamations';
import { FlagEmpty } from '@/ui/icons/flag-empty';
import { FlagFilled } from '@/ui/icons/flag-filled';
import { Location } from '@/ui/icons/location';
import { Pin } from '@/ui/icons/pin';
import { Seat } from '@/ui/icons/seat';
import { SteeringWheel } from '@/ui/icons/steering-wheel';
import { Loader } from '@/ui/loader';
import ShowMore from '@/ui/show-more';

const PAGE_WIDTH = Dimensions.get('window').width;

type CancellationOptionsSectionProps = {
  options: CancellationPolicy[] | undefined;
  selectedId: number | undefined;
  onSelect: (option: CancellationPolicy) => void;
  onCompare: () => void;
  car: Car;
};

function CancellationOptionsSection({
  options,
  selectedId,
  onSelect,
  onCompare,
  car,
}: CancellationOptionsSectionProps) {
  return (
    <View style={{ gap: 16 }}>
      <Text type="title2">Cancellation Options</Text>

      <View style={{ gap: 16 }}>
        {options?.map((option) => {
          return (
            <CancellationPolicyCardItem
              key={option.id}
              cancellationPolicy={option}
              car={car}
              style={{ width: '100%' }}
              active={selectedId === option.id}
              onPress={() => {
                onSelect(option);
              }}
            />
          );
        })}
      </View>

      <Button
        text="Compare Cancellation options"
        type="ghost"
        onPress={onCompare}
        style={{ width: 200, paddingLeft: 0 }}
        textStyle={{ fontSize: 12, textDecorationLine: 'underline' }}
      />
    </View>
  );
}

export default function Car() {
  const { styles, theme } = useStyles(stylesheet);
  const router = useRouter();
  const local = useLocalSearchParams<{
    id: string;
    start_datetime: string;
    end_datetime: string;
    delivery_address?: string;
    delivery_address_lat?: string;
    delivery_address_lng?: string;
    return_address?: string;
    return_address_lat?: string;
    return_address_lng?: string;
    insurance_id?: string;
    code?: string;
    mode?: 'pickup' | 'delivery';
  }>();
  const progress = useSharedValue<number>(0);
  const carouselRef = React.useRef<ICarouselInstance>(null);

  const searchDeliveryDeliverToAddress = useSearchStore(
    (state) => state.deliveryDeliverToAddress
  );
  const searchDeliveryReturnAddress = useSearchStore(
    (state) => state.deliveryReturnAddress
  );
  const searchDeliveryModeEnabled = useSearchStore(
    (state) => state.deliveryModeEnabled
  );
  const { data: customer } = useCustomer();

  const status = useAuth.use.status();
  const address = useBookCarStore((state) => state.address);
  const setAddress = useBookCarStore((state) => state.setAddress);
  const startDatetime = useBookCarStore((state) => state.startDatetime);
  const setStartDatetime = useBookCarStore((state) => state.setStartDatetime);
  const endDatetime = useBookCarStore((state) => state.endDatetime);
  const setEndDatetime = useBookCarStore((state) => state.setEndDatetime);
  const insurance = useBookCarStore((state) => state.insurance);
  const setInsurance = useBookCarStore((state) => state.setInsurance);
  const cancellationPolicy = useBookCarStore(
    (state) => state.cancellationPolicy
  );
  const setCancellationPolicy = useBookCarStore(
    (state) => state.setCancellationPolicy
  );
  const deliveryModeEnabled = useBookCarStore(
    (state) => state.deliveryModeEnabled
  );
  const setDeliveryModeEnabled = useBookCarStore(
    (state) => state.setDeliveryModeEnabled
  );
  const deliveryDeliverToAddress = useBookCarStore(
    (state) => state.deliveryDeliverToAddress
  );
  const setDeliveryDeliverToAddress = useBookCarStore(
    (state) => state.setDeliveryDeliverToAddress
  );
  const deliveryReturnAddress = useBookCarStore(
    (state) => state.deliveryReturnAddress
  );
  const setDeliveryReturnToAddress = useBookCarStore(
    (state) => state.setDeliveryReturnToAddress
  );
  const car = useBookCarStore((state) => state.car);
  const setCar = useBookCarStore((state) => state.setCar);
  const discount = useBookCarStore((state) => state.discount);
  const setDiscount = useBookCarStore((state) => state.setDiscount);
  const [checkedReturnAddress, setCheckedReturnAddress] = React.useState(false);
  const [showHowDeliveryFeesModal, setShowHowDeliveryFeesModal] =
    React.useState(false);
  const [showNoInsuranceModal, setShowNoInsuranceModal] = React.useState(false);
  const [discountCode, setDiscountCode] = React.useState('');
  const [showRentalBreakdownModal, setShowRentalBreakdownModal] =
    React.useState(false);
  const [checkingOut, setCheckingOut] = React.useState(false);
  const [showFinishProfileModal, setShowFinishProfileModal] =
    React.useState(false);
  const [contentWidth, setContentWidth] = React.useState(0);
  const [carouselActiveIndex, setCarouselActiveIndex] = React.useState(0);
  const scrollOffsetValue = useSharedValue<number>(0);
  const navbarRef = React.useRef();
  const [navbarPosition, setNavbarPosition] = React.useState({
    top: 0,
    left: 0,
    x: 0,
    y: 0,
    width: 0,
    height: 0,
  });
  const stickyBarRef = React.useRef();
  const [stickyBarPosition, setStickyBarPosition] = React.useState({
    top: 0,
    left: 0,
    x: 0,
    y: 0,
    width: 0,
    height: 0,
  });
  const [isSticky, setIsSticky] = React.useState(false);
  const [
    showCompareCancellationPoliciesModal,
    setShowCompareCancellationPoliciesModal,
  ] = React.useState(false);
  const [collapseLocationDetails, setCollapseLocationDetails] =
    React.useState(true);
  const { organization } = useCurrentOrganization();

  const loadedGoogle = useGoogleMapsApi();

  const trackMetaEvent = useTrackMetaEvent();
  // Guards Meta ViewContent so it fires once per car (carData refetches when the
  // user changes dates/delivery), without re-firing on every refetch.
  const viewContentFiredForRef = React.useRef<string | null>(null);

  useFocusEffect(
    React.useCallback(() => {
      if (status === 'signIn' && checkingOut && customer?.setupComplete) {
        setCheckingOut(false);
        router.push('/checkout');
      }
    }, [checkingOut, status, router, customer])
  );

  const {
    data: carData,
    isPending,
    isError,
    isFetching,
    // error,
    refetch: refetchCar,
  } = useCar({
    //@ts-ignore
    variables: {
      id: local.id,
      startDatetime: format(startDatetime, 'yyyy-MM-dd HH:mm:ss'),
      endDatetime: format(endDatetime, 'yyyy-MM-dd HH:mm:ss'),
      deliveryAddress:
        (deliveryModeEnabled &&
          deliveryReturnAddress &&
          deliveryDeliverToAddress?.description) ||
        '',
      returnAddress:
        (deliveryModeEnabled &&
          deliveryDeliverToAddress &&
          deliveryReturnAddress?.description) ||
        '',
    },
    retry: false,
    enabled: false,
  });

  React.useEffect(() => {
    if (!carData) return;

    mixpanelService.track('Page Viewed', {
      'Page Name': 'Car Details',
      'Selected Trip Days': carData.meta.numDays,
    });

    const viewedCar = carData.data;
    if (viewContentFiredForRef.current !== String(local.id) && viewedCar) {
      viewContentFiredForRef.current = String(local.id);

      const dailyRate = viewedCar.discountedDailyRate || viewedCar.dailyRate;

      trackMetaEvent('ViewContent', {
        type: 'daily',
        customData: {
          ...getCarContentData(viewedCar, {
            category: 'daily_rental',
            quantity: carData.meta.numDays,
            itemPrice: dailyRate,
          }),
          value: dailyRate * carData.meta.numDays,
          currency: 'USD',
        },
      });
    }
  }, [carData]);

  const {
    data: isCarAvailableData,
    refetch: refetchIsCarAvailable,
    error: carAvailableError,
    isError: isCarAvailableError,
  } = useCarAvailable({
    //@ts-ignore
    variables: {
      id: local.id,
      startDatetime: format(startDatetime, 'yyyy-MM-dd HH:mm:ss'),
      endDatetime: format(endDatetime, 'yyyy-MM-dd HH:mm:ss'),
      deliveryAddress:
        (deliveryModeEnabled &&
          deliveryReturnAddress &&
          deliveryDeliverToAddress?.description) ||
        '',
      returnAddress:
        (deliveryModeEnabled &&
          deliveryDeliverToAddress &&
          deliveryReturnAddress?.description) ||
        '',
    },
    retry: false,
    enabled: false,
  });

  const colorImgSrc = React.useMemo(() => {
    if (!car) return '';

    return getColorImgSrc(car.color);
  }, [car]);

  React.useEffect(() => {
    if (local.start_datetime) {
      setStartDatetime(new Date(local.start_datetime));
    }
  }, [local.start_datetime, setStartDatetime]);

  React.useEffect(() => {
    if (local.end_datetime) {
      setEndDatetime(new Date(local.end_datetime));
    }
  }, [local.end_datetime, setEndDatetime]);

  React.useEffect(() => {
    setDeliveryModeEnabled(local.mode === 'delivery');
  }, [local.mode]);

  React.useEffect(() => {
    if (
      local.mode === 'delivery' &&
      local.delivery_address &&
      local.return_address &&
      local.delivery_address === local.return_address
    ) {
      setCheckedReturnAddress(true);
    }
  }, [local.delivery_address, local.return_address, local.mode]);

  React.useEffect(() => {
    if (!carData || !carData.data) return;

    setCar(carData.data);
  }, [carData?.data]);

  const carTimezone = car?.defaultLocation?.timezone;

  const startTimeInCarTimezone = React.useMemo(() => {
    if (!startDatetime) return undefined;
    if (!carTimezone) return undefined;

    return DateTime.fromJSDate(startDatetime)
      .setZone(carTimezone, { keepLocalTime: true })
      .toJSDate();
  }, [startDatetime, carTimezone]);

  const { data: addOns } = useAddOns({
    variables: { startTime: startTimeInCarTimezone! },
    enabled: !!startTimeInCarTimezone,
  });

  React.useEffect(() => {
    const fetchData = async () => {
      try {
        setIsSticky(false);
        await refetchIsCarAvailable();
        await refetchCar();
      } catch (error) {
        showErrorMessage('Failed to fetch car availability. Please try again.');
      }
    };

    if (
      local.start_datetime &&
      !isEqual(startDatetime, new Date(local.start_datetime))
    ) {
      return;
    }

    if (
      local.end_datetime &&
      !isEqual(endDatetime, new Date(local.end_datetime))
    ) {
      return;
    }

    if (
      (deliveryDeliverToAddress && !deliveryReturnAddress) ||
      (!deliveryDeliverToAddress && deliveryReturnAddress)
    ) {
      return;
    }

    if (
      startDatetime &&
      endDatetime &&
      deliveryDeliverToAddress &&
      deliveryReturnAddress
    ) {
      fetchData();
      return;
    }

    if (startDatetime && endDatetime) {
      fetchData();
      return;
    }
  }, [
    startDatetime,
    endDatetime,
    deliveryModeEnabled,
    deliveryDeliverToAddress,
    deliveryReturnAddress,
    local.start_datetime,
    local.end_datetime,
  ]);

  React.useEffect(() => {
    if (
      local.delivery_address &&
      local.delivery_address_lat &&
      local.delivery_address_lng
    ) {
      setDeliveryDeliverToAddress({
        description: local.delivery_address || '',
        latitude: parseFloat(local.delivery_address_lat),
        longitude: parseFloat(local.delivery_address_lng),
      });
    }
  }, [
    local.delivery_address,
    local.delivery_address_lat,
    local.delivery_address_lng,
    setDeliveryDeliverToAddress,
  ]);

  React.useEffect(() => {
    if (
      local.return_address &&
      local.return_address_lat &&
      local.return_address_lng
    ) {
      setDeliveryReturnToAddress({
        description: local.return_address || '',
        latitude: parseFloat(local.return_address_lat),
        longitude: parseFloat(local.return_address_lng),
      });
    }
  }, [
    local.return_address,
    local.return_address_lat,
    local.return_address_lng,
    setDeliveryReturnToAddress,
  ]);

  React.useEffect(() => {
    if (local.code) {
      setDiscountCode(local.code);
    }
  }, [local.code]);

  React.useEffect(() => {
    setAddress(car?.defaultLocation);
  }, [car?.defaultLocation, setAddress]);

  React.useEffect(() => {
    if (!addOns || !addOns.insuranceOptions) return;

    const selectedInsurance = addOns.insuranceOptions.find(
      (option) => option.id === parseInt(local.insurance_id)
    );

    if (selectedInsurance) setInsurance(selectedInsurance);
  }, [addOns, local.insurance_id, setInsurance]);

  React.useEffect(() => {
    if (insurance || !addOns || !addOns.insuranceOptions) return;

    const selectedInsurance = addOns.insuranceOptions.find(
      (option) => option.name === 'Standard Protection'
    );

    setInsurance(selectedInsurance);
  }, [insurance, addOns]);

  React.useEffect(() => {
    if (cancellationPolicy || !addOns || !addOns.cancellationOptions) return;

    const selectedCancellationPolicy = addOns.cancellationOptions.find(
      (option) => option.title === 'Non-Refundable'
    );

    setCancellationPolicy(selectedCancellationPolicy);
  }, [cancellationPolicy, addOns]);

  React.useEffect(() => {
    if (Platform.OS !== 'web') return;

    if (insurance) {
      const url = new URL(window.location.href);
      url.searchParams.set('insurance_id', insurance.id.toString());
      window.history.replaceState({}, '', url.toString());
    }
  }, [insurance]);

  React.useEffect(() => {
    if (Platform.OS !== 'web') return;

    // if (!deliveryDeliverToAddress) {
    //   const url = new URL(window.location.href);

    //   url.searchParams.delete('delivery_address');
    //   url.searchParams.delete('delivery_address_lat');
    //   url.searchParams.delete('delivery_address_lng');

    //   window.history.replaceState({}, '', url.toString());

    //   setDeliveryDeliverToAddress(null);
    //   // setDeliveryReturnToAddress(null);
    // }

    // if (!deliveryReturnAddress) {
    //   const url = new URL(window.location.href);

    //   url.searchParams.delete('return_address');
    //   url.searchParams.delete('return_address_lat');
    //   url.searchParams.delete('return_address_lng');

    //   window.history.replaceState({}, '', url.toString());

    //   setDeliveryReturnToAddress(null);
    // }

    if (
      deliveryModeEnabled &&
      deliveryDeliverToAddress &&
      deliveryReturnAddress
    ) {
      const url = new URL(window.location.href);

      url.searchParams.set(
        'delivery_address',
        deliveryDeliverToAddress.description
      );
      url.searchParams.set(
        'delivery_address_lat',
        deliveryDeliverToAddress.latitude.toString()
      );
      url.searchParams.set(
        'delivery_address_lng',
        deliveryDeliverToAddress.longitude.toString()
      );

      url.searchParams.set('return_address', deliveryReturnAddress.description);
      url.searchParams.set(
        'return_address_lat',
        deliveryReturnAddress.latitude.toString()
      );
      url.searchParams.set(
        'return_address_lng',
        deliveryReturnAddress.longitude.toString()
      );

      window.history.replaceState({}, '', url.toString());
    }
  }, [deliveryDeliverToAddress, deliveryReturnAddress, deliveryModeEnabled]);

  // const polylines = React.useMemo(() => {
  //   if (!car) return;

  //   return car.defaultLocation &&
  //     deliveryModeEnabled &&
  //     deliveryDeliverToAddress &&
  //     deliveryReturnAddress
  //     ? [
  //         {
  //           coordinates: [
  //             {
  //               latitude: car.defaultLocation.latitude,
  //               longitude: car.defaultLocation.longitude,
  //             },
  //             {
  //               latitude: deliveryDeliverToAddress.latitude,
  //               longitude: deliveryDeliverToAddress.longitude,
  //             },
  //           ],
  //           strokeColor: PINKLING,
  //           strokeWidth: 2,
  //         },
  //         {
  //           coordinates: [
  //             {
  //               latitude: car.defaultLocation.latitude,
  //               longitude: car.defaultLocation.longitude,
  //             },
  //             {
  //               latitude: deliveryReturnAddress.latitude,
  //               longitude: deliveryReturnAddress.longitude,
  //             },
  //           ],
  //           strokeColor: PINKLING,
  //           strokeWidth: 2,
  //         },
  //       ]
  //     : [];
  // }, [
  //   car,
  //   deliveryModeEnabled,
  //   deliveryDeliverToAddress,
  //   deliveryReturnAddress,
  // ]);

  const markers = React.useMemo(() => {
    if (!car) return [];

    let ms: CarMarkerProps[] = [];

    ms = [
      ...ms,
      {
        latitude: car.defaultLocation.latitude,
        longitude: car.defaultLocation.longitude,
        icon: (
          <Icon
            type="naked"
            icon={<CarIcon />}
            fillColor={theme.colors.icon.color}
            width={20}
            height={20}
            style={{ borderColor: 'red', borderWidth: 1 }}
          />
        ),
      },
    ];

    if (
      deliveryModeEnabled &&
      deliveryDeliverToAddress &&
      deliveryReturnAddress
    ) {
      ms = [
        ...ms,
        {
          latitude: deliveryDeliverToAddress.latitude,
          longitude: deliveryDeliverToAddress.longitude,
          icon: (
            <Icon
              type="naked"
              icon={<FlagEmpty />}
              fillColor={theme.colors.icon.color}
              width={20}
              height={20}
            />
          ),
          // active: true,
        },
        {
          latitude: deliveryReturnAddress.latitude,
          longitude: deliveryReturnAddress.longitude,
          icon: (
            <Icon
              type="naked"
              icon={<FlagFilled />}
              fillColor={theme.colors.icon.color}
              width={20}
              height={20}
            />
          ),
          // active: true,
        },
      ];
    }

    // if (
    //   car.defaultLocation &&
    //   deliveryModeEnabled &&
    //   deliveryDeliverToAddress &&
    //   deliveryReturnAddress
    // ) {
    //   ms = [
    //     ...ms,
    //     {
    //       latitude: midpoint(
    //         car.defaultLocation.latitude,
    //         car.defaultLocation.longitude,
    //         deliveryDeliverToAddress.latitude,
    //         deliveryDeliverToAddress.longitude
    //       ).latitude,
    //       longitude: midpoint(
    //         car.defaultLocation.latitude,
    //         car.defaultLocation.longitude,
    //         deliveryDeliverToAddress.latitude,
    //         deliveryDeliverToAddress.longitude
    //       ).longitude,
    //       text: `${formatNumber(car.deliveryDistance)} mi`,
    //       active: true,
    //     },
    //     {
    //       latitude: midpoint(
    //         car.defaultLocation.latitude,
    //         car.defaultLocation.longitude,
    //         deliveryReturnAddress.latitude,
    //         deliveryReturnAddress.longitude
    //       ).latitude,
    //       longitude: midpoint(
    //         car.defaultLocation.latitude,
    //         car.defaultLocation.longitude,
    //         deliveryReturnAddress.latitude,
    //         deliveryReturnAddress.longitude
    //       ).longitude,
    //       text: `${formatNumber(car.returnDistance)} mi`,
    //       active: true,
    //     },
    //   ];
    // }

    return ms;
  }, [
    car,
    deliveryModeEnabled,
    deliveryDeliverToAddress,
    deliveryReturnAddress,
  ]);

  const defaultCenter = React.useMemo(() => {
    if (!car) return;

    if (
      deliveryModeEnabled &&
      deliveryDeliverToAddress &&
      deliveryReturnAddress
    ) {
      const midpt = midpoint(
        deliveryDeliverToAddress.latitude,
        deliveryDeliverToAddress.longitude,
        deliveryReturnAddress.latitude,
        deliveryReturnAddress.longitude
      );

      return midpt;
    }

    if (car.defaultLocation) {
      return {
        latitude: car.defaultLocation.latitude,
        longitude: car.defaultLocation.longitude,
      };
    }

    return { latitude: 40.758916, longitude: -73.967476 };
  }, [
    car,
    deliveryModeEnabled,
    deliveryDeliverToAddress,
    deliveryReturnAddress,
  ]);

  const calculatedCost = React.useMemo(() => {
    if (!car || !carData) return 0;

    let cost = 0;

    if (car.discountedDailyRate) {
      cost = car.discountedDailyRate * carData.meta.numDays;
    } else {
      cost = car.dailyRate * carData.meta.numDays;
    }

    if (insurance) {
      cost +=
        getDailyInsuranceCost(insurance, car.dailyRate) * carData.meta.numDays;
    }

    if (cancellationPolicy) {
      cost +=
        (cancellationPolicy.percentage / 100) *
        car.dailyRate *
        carData.meta.numDays;
    }

    if (car && car.deliveryCost > 0) {
      cost += car.deliveryCost;
    }

    if (discount) {
      cost -= discount.amount;
    }

    return cost;
  }, [car, insurance, cancellationPolicy, carData?.meta.numDays, discount]);

  const showCancellationOptions = React.useMemo(() => {
    if (organization) {
      return false;
    }

    return true;
  }, [organization]);

  const tracking = useCarDetailTracking({
    car,
    carMeta: carData?.meta,
    insurance,
    cancellationPolicy,
    discount,
    deliveryModeEnabled,
    calculatedCost,
  });

  const handleInsuranceSelect = React.useCallback(
    (option: Insurance) => {
      tracking.trackInsuranceChange(option);

      if (insurance?.id !== option.id && option.name === 'No Protection') {
        setShowNoInsuranceModal(true);
      }

      setInsurance(option);
    },
    [insurance, setInsurance, tracking]
  );

  const handleCancellationPolicySelect = React.useCallback(
    (option: CancellationPolicy) => {
      tracking.trackCancellationPolicyChange(option);
      setCancellationPolicy(option);
    },
    [setCancellationPolicy, tracking]
  );

  const handleCheckout = React.useCallback(() => {
    if (
      deliveryModeEnabled &&
      (!deliveryDeliverToAddress || !deliveryReturnAddress)
    ) {
      showErrorMessage('Please enter a delivery and return address');
      return;
    }

    tracking.trackBookingCustomized();
    if (status !== 'signIn') {
      router.push('/login?returnTo=/checkout');
      return;
    }

    setCheckingOut(true);

    if (!customer?.setupComplete) {
      setShowFinishProfileModal(true);
    }
  }, [
    deliveryModeEnabled,
    deliveryDeliverToAddress,
    deliveryReturnAddress,
    status,
    customer,
    router,
    tracking,
  ]);

  const startAddress = React.useMemo(() => {
    if (deliveryModeEnabled && deliveryDeliverToAddress) {
      return deliveryDeliverToAddress.description;
    }

    if (!deliveryModeEnabled) {
      return car?.defaultLocation.address;
    }

    return '';
  }, [deliveryModeEnabled, deliveryDeliverToAddress, car]);

  const endAddress = React.useMemo(() => {
    if (deliveryModeEnabled && deliveryReturnAddress) {
      return deliveryReturnAddress.description;
    }

    if (!deliveryModeEnabled) {
      return car?.defaultLocation.address;
    }

    return '';
  }, [deliveryModeEnabled, deliveryReturnAddress, car]);

  const onPressPagination = (index: number) => {
    carouselRef.current?.scrollTo({
      /**
       * Calculate the difference between the current index and the target index
       * to ensure that the carousel scrolls to the nearest index
       */
      count: index - progress.value,
      animated: false,
    });

    setCarouselActiveIndex(index);
  };

  if (!car || !carData || isFetching) {
    return (
      <View style={{ flex: 1, backgroundColor: theme.colors.background_1 }}>
        <Stack.Screen
          options={{
            headerTitle: '',
            headerLeft: () => null,
            headerStyle: {
              backgroundColor: theme.colors.background_1,
            },
            headerTintColor: theme.colors.text.primary,
            headerShadowVisible: false,
            headerBackVisible: false,
          }}
        />

        <View
          style={{
            width: '100%',
            height: '100%',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Loader style={{ height: 250, width: 250 }} />
        </View>
      </View>
    );
  }

  if (isDesktop()) {
    return (
      <View style={styles.container}>
        <Stack.Screen
          options={{
            headerShown: false,
          }}
        />

        <View
          ref={navbarRef}
          style={{
            height: 20,
          }}
          onLayout={(event) => {
            if (navbarRef.current) {
              navbarRef.current.measure((x, y, width, height, pageX, pageY) => {
                setNavbarPosition({
                  top: pageY,
                  left: pageX,
                  x,
                  y,
                  width,
                  height,
                });
              });
            }
          }}
        />

        <View style={styles.scrollViewContainer}>
          <ScrollView
            showsVerticalScrollIndicator={false}
            style={{
              width: '100%',
            }}
            contentContainerStyle={{
              alignItems: 'center',
            }}
            scrollEventThrottle={4}
            onScroll={(event) => {
              const scrollY = event.nativeEvent.contentOffset.y;

              if (
                scrollY >= stickyBarPosition.top - navbarPosition.height &&
                !isSticky
              ) {
                setIsSticky(true);
              } else if (
                scrollY < stickyBarPosition.top - navbarPosition.height &&
                isSticky
              ) {
                setIsSticky(false);
              }
            }}
          >
            <View
              style={styles.scrollViewChild}
              onLayout={(event) => {
                const { width } = event.nativeEvent.layout;
                setContentWidth(width);
              }}
            >
              {contentWidth ? (
                <View
                  style={{
                    width: '100%',
                    height: 600,
                    position: 'relative',
                  }}
                  id="carousel-component"
                  dataSet={{ kind: 'basic-layouts', name: 'parallax' }}
                >
                  {carouselActiveIndex > 0 ? (
                    <Pressable
                      style={{
                        position: 'absolute',
                        top: '50%',
                        left: 0,
                        zIndex: 1,
                      }}
                      onPress={() => {
                        carouselRef.current?.scrollTo({
                          count: -1,
                          animated: true,
                        });
                      }}
                    >
                      <Icon
                        type="dressed"
                        icon={<ChevronLeft />}
                        fillColor={theme.colors.icon.color}
                        width={12}
                        height={12}
                        style={{ width: 36, height: 36 }}
                      />
                    </Pressable>
                  ) : null}

                  <Carousel
                    ref={carouselRef}
                    defaultScrollOffsetValue={scrollOffsetValue}
                    width={contentWidth}
                    style={{
                      width: '100%',
                    }}
                    height={600}
                    data={getCarImages(car.images)}
                    mode="parallax"
                    modeConfig={{
                      parallaxScrollingScale: 1,
                      parallaxScrollingOffset: 50,
                    }}
                    onConfigurePanGesture={(g) => {
                      'worklet';
                      g.enabled(false);
                    }}
                    pagingEnabled
                    windowSize={1}
                    loop={false}
                    onProgressChange={progress}
                    onSnapToItem={(index) => {
                      setCarouselActiveIndex(index);
                    }}
                    renderItem={({ item }) => {
                      return (
                        <Image
                          source={{ uri: item.url }}
                          style={{
                            width: '100%',
                            aspectRatio: 16 / 9,
                            borderRadius: 16,
                          }}
                          contentFit="cover"
                        />
                      );
                    }}
                  />

                  {carouselActiveIndex < 4 ? (
                    <Pressable
                      style={{
                        position: 'absolute',
                        top: '50%',
                        right: 0,
                        zIndex: 1,
                      }}
                      onPress={() => {
                        carouselRef.current?.scrollTo({
                          count: 1,
                          animated: true,
                        });
                      }}
                    >
                      <Icon
                        type="dressed"
                        icon={<ChevronRight />}
                        fillColor={theme.colors.icon.color}
                        width={12}
                        height={12}
                        style={{ width: 36, height: 36 }}
                      />
                    </Pressable>
                  ) : null}

                  <Pagination.Basic
                    progress={progress}
                    data={getCarImages(car.images)}
                    dotStyle={{
                      backgroundColor: theme.colors.paginationDots.empty,
                      borderRadius: 50,
                    }}
                    activeDotStyle={{
                      backgroundColor: theme.colors.paginationDots.filled,
                    }}
                    size={8}
                    containerStyle={{ gap: 10, marginTop: 20 }}
                    onPress={onPressPagination}
                  />
                </View>
              ) : null}

              <View style={{ flexDirection: 'row', gap: 32 }}>
                <View style={{ width: '65%', gap: 16 }}>
                  <View style={{ gap: 16 }}>
                    <View style={{ flexDirection: 'row', gap: 8 }}>
                      <View
                        style={{
                          backgroundColor: theme.colors.icon.fill,
                          padding: 10,
                          borderRadius: 8,
                          width: 40,
                          height: 40,
                          justifyContent: 'center',
                          alignItems: 'center',
                        }}
                      >
                        <Image
                          style={{
                            width: 26,
                            height: 26,
                          }}
                          source={getCarBrandLogo(car.brandLogos)}
                          contentFit="cover"
                        />
                      </View>

                      <View>
                        <Text type="title1">{`${car.brandName} ${car.modelName}`}</Text>
                        <Text
                          type="bodyRegular"
                          style={{ color: theme.colors.text.secondary }}
                        >
                          {car.modelType}
                        </Text>
                      </View>
                    </View>

                    <View style={{ gap: 8 }}>
                      <View style={{ flexDirection: 'row', gap: 8 }}>
                        <Chip
                          icon={<SteeringWheel />}
                          text={car.autopilotType}
                        />

                        <Chip
                          icon={<Battery />}
                          text={`${car.batteryRange} Miles Per Charge`}
                        />

                        <Chip icon={<Seat />} text={`${car.numSeats} Seats`} />

                        <Chip
                          imgSrc={colorImgSrc}
                          useDefaultIconFillColor={false}
                          text="Exterior"
                        />
                      </View>
                    </View>

                    <View>
                      <Text
                        type="bodyRegular"
                        style={{ color: theme.colors.text.secondary }}
                      >
                        {car.description}
                      </Text>
                    </View>
                  </View>

                  <View
                    style={{
                      width: '100%',
                      backgroundColor: theme.colors.card.default.borderColor,
                      height: 1,
                    }}
                  />

                  <View
                    style={{
                      gap: 16,
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Text type="title2" tx="carId.carLocation" />

                    <Button
                      type="link"
                      onPress={() => {
                        openAddressInMaps(car.defaultLocation.address);
                      }}
                      textStyle={{ fontSize: 12 }}
                      style={{
                        width: 'inherit',
                        height: 20,
                        paddingTop: 0,
                        paddingRight: 0,
                        paddingBottom: 0,
                        paddingLeft: 0,
                        alignItems: 'flex-start',
                        justifyContent: 'flex-start',
                      }}
                    >
                      <Text
                        style={{
                          textDecorationLine: 'underline',
                          color: theme.colors.text.secondary,
                        }}
                        type="bodyRegular"
                      >
                        {car.defaultLocation.address}
                      </Text>
                    </Button>
                  </View>

                  <View
                    style={{
                      width: '100%',
                      height: 300,
                      borderRadius: 16,
                      overflow: 'hidden',
                      position: 'relative',
                      zIndex: 1,
                    }}
                  >
                    <MapView
                      polylines={[]}
                      markers={markers}
                      defaultCenter={defaultCenter}
                      containerStyle={{ borderRadius: 16 }}
                      scrollEnabled={false}
                      zoomEnabled={false}
                      fitBounds={Boolean(
                        deliveryModeEnabled &&
                          deliveryDeliverToAddress &&
                          deliveryReturnAddress
                      )}
                      altitude={3000}
                      zoom={16}
                      showsUserLocation={false}
                    />
                  </View>

                  {car.supportsDelivery ? (
                    <View style={{ gap: 16, flexDirection: 'row' }}>
                      <CardItem
                        key="pickupAndReturn"
                        icon={
                          <Icon
                            type="dressed"
                            icon={<Pin />}
                            fillColor={theme.colors.icon.color}
                            width={24}
                            height={24}
                          />
                        }
                        title={t('carId.pickupAndReturnDetails')}
                        body={t('carId.pickupAndReturnBody')}
                        active={!deliveryModeEnabled}
                        onPress={() => {
                          setDeliveryModeEnabled(false);

                          const url = new URL(window.location.href);

                          url.searchParams.set('mode', 'pickup');

                          window.history.replaceState({}, '', url.toString());
                        }}
                      />

                      <CardItem
                        key="delivery"
                        icon={
                          <Icon
                            type="dressed"
                            icon={<Location />}
                            fillColor={theme.colors.icon.color}
                            width={24}
                            height={24}
                          />
                        }
                        title={t('carId.delivery')}
                        body={t('carId.deliveryBody')}
                        active={deliveryModeEnabled}
                        onPress={() => {
                          setDeliveryModeEnabled(true);

                          const url = new URL(window.location.href);

                          url.searchParams.set('mode', 'delivery');

                          window.history.replaceState({}, '', url.toString());
                        }}
                      />
                    </View>
                  ) : null}

                  {deliveryModeEnabled ? (
                    <View
                      style={{
                        borderRadius: 16,
                        flexDirection: 'column',
                        gap: 24,
                        zIndex: 1,
                      }}
                    >
                      <View style={{ gap: 8, zIndex: 2 }}>
                        {isCarAvailableError ? (
                          <View>
                            <Text style={{ color: 'red' }}>
                              {carAvailableError.message}
                            </Text>
                          </View>
                        ) : null}

                        <Text type="bodyRegular" tx="search.deliveryAddress" />

                        {loadedGoogle ? (
                          <View
                            style={{
                              flexGrow: 1,
                              width: '100%',
                            }}
                          >
                            <GoogleAutocomplete
                              label="Delivery Address"
                              placeholder="Delivery address"
                              onPress={(place: Address) => {
                                setDeliveryDeliverToAddress(place);
                              }}
                              inputStyle={{ width: 300 }}
                              address={deliveryDeliverToAddress}
                              onClear={() => {
                                setDeliveryDeliverToAddress(null);
                              }}
                              autofocus={false}
                            />
                          </View>
                        ) : null}
                      </View>

                      <View style={{ gap: 8, zIndex: 1 }}>
                        <Text type="bodyRegular" tx="search.returnAddress" />

                        {loadedGoogle ? (
                          <View style={{ flexGrow: 1, width: '100%' }}>
                            <GoogleAutocomplete
                              label="Return Address"
                              placeholder="Return address"
                              onPress={(place: Address) => {
                                setDeliveryReturnToAddress(place);
                              }}
                              inputStyle={{ width: 300 }}
                              address={deliveryReturnAddress}
                              disabled={checkedReturnAddress}
                              onClear={() => {
                                setDeliveryReturnToAddress(null);
                              }}
                              autofocus={false}
                            />
                          </View>
                        ) : null}
                      </View>

                      {deliveryDeliverToAddress ? (
                        <View>
                          <CheckItem
                            text="Return address is the same address"
                            onPress={() => {
                              if (checkedReturnAddress) {
                                setDeliveryReturnToAddress(null);
                              } else {
                                setDeliveryReturnToAddress(
                                  deliveryDeliverToAddress
                                );
                              }

                              setCheckedReturnAddress(!checkedReturnAddress);
                            }}
                            active={checkedReturnAddress}
                            type="checkbox"
                          />
                        </View>
                      ) : null}

                      {!isCarAvailableError ? (
                        <Card
                          style={{
                            paddingLeft: 16,
                            paddingRight: 16,
                            paddingTop: 24,
                            paddingBottom: 24,
                          }}
                        >
                          <Pressable
                            hitSlop={8}
                            onPress={() => {
                              setShowHowDeliveryFeesModal(true);
                            }}
                          >
                            <View
                              style={{
                                flexDirection: 'row',
                                justifyContent: 'space-between',
                              }}
                            >
                              <View
                                style={{
                                  flexDirection: 'row',
                                  gap: 8,
                                  alignItems: 'center',
                                }}
                              >
                                <Text
                                  type="bodyRegular"
                                  tx="carId.deliveryFee"
                                />

                                <Icon
                                  type="naked"
                                  icon={<Info />}
                                  fillColor={theme.colors.icon.color}
                                  width={15}
                                  height={15}
                                />
                              </View>

                              <View>
                                <Text type="bodyBold">{`+$${car.deliveryCost}`}</Text>
                              </View>
                            </View>
                          </Pressable>
                        </Card>
                      ) : null}
                    </View>
                  ) : null}

                  <View
                    style={{
                      width: '100%',
                      backgroundColor: theme.colors.card.default.borderColor,
                      height: 1,
                    }}
                  />

                  <View style={{ gap: 24 }}>
                    <Text tx="carId.insurancePlans" type="title2" />

                    <View style={{ gap: 16 }}>
                      {addOns?.insuranceOptions.map((option) => {
                        return (
                          <InsuranceCardItem
                            key={option.name}
                            insurance={option}
                            car={car}
                            style={{ width: '100%' }}
                            active={insurance?.id === option.id}
                            onPress={() => handleInsuranceSelect(option)}
                          />
                        );
                      })}
                    </View>
                  </View>

                  {showCancellationOptions ? (
                    <>
                      <View
                        style={{
                          width: '100%',
                          backgroundColor:
                            theme.colors.card.default.borderColor,
                          height: 1,
                        }}
                      />
                      <CancellationOptionsSection
                        options={addOns?.cancellationOptions}
                        selectedId={cancellationPolicy?.id}
                        onSelect={handleCancellationPolicySelect}
                        onCompare={() => {
                          setShowCompareCancellationPoliciesModal(true);
                        }}
                        car={car}
                      />
                    </>
                  ) : null}

                  <View
                    style={{
                      width: '100%',
                      backgroundColor: theme.colors.card.default.borderColor,
                      height: 1,
                    }}
                  />

                  <View style={{ paddingBottom: 32 }}>
                    <Discount
                      title={t('discount.addDiscountCode')}
                      inputLabel={t('discount.enterDiscountCodeLabel')}
                      buttonTx={'discount.redeemCode'}
                      onChangeText={(text: string) => {
                        setDiscountCode(text);
                      }}
                      onPress={() => {}}
                      onSuccess={(d: DiscountType) => {
                        setDiscount(d);
                      }}
                      disabled={discountCode.length === 0}
                      defaultText={local.code}
                    />
                  </View>
                </View>

                <View
                  ref={stickyBarRef}
                  onLayout={(e) => {
                    if (
                      stickyBarRef.current &&
                      !isSticky &&
                      !stickyBarPosition?.top
                    ) {
                      stickyBarRef.current.measure(
                        (x, y, width, height, pageX, pageY) => {
                          setStickyBarPosition({
                            top: pageY,
                            left: pageX,
                            x,
                            y,
                          });
                        }
                      );
                    }
                  }}
                >
                  {!isSticky ? (
                    <View
                      style={{
                        width: contentWidth * 0.35,
                        gap: 24,
                      }}
                    >
                      <Collapsible
                        collapsed={collapseLocationDetails}
                        onToggle={() => {
                          setCollapseLocationDetails(!collapseLocationDetails);
                        }}
                        title={t(
                          deliveryModeEnabled
                            ? 'carId.deliveryAndReturnDetails'
                            : 'carId.pickupAndReturnDetails'
                        )}
                      >
                        <View style={{ flex: 1, padding: 16, gap: 4 }}>
                          <View style={{ gap: 8, flexDirection: 'row' }}>
                            <View style={{ flex: 1 }}>
                              <Text
                                tx={
                                  deliveryModeEnabled
                                    ? 'carId.deliveryLocation'
                                    : 'carId.pickupLocation'
                                }
                                type="bodyBold"
                              />

                              <Text
                                type="bodyRegular"
                                style={{ color: theme.colors.text.secondary }}
                              >
                                {format(startDatetime, 'MMM dd - hh:mm a')}
                              </Text>

                              {startAddress ? (
                                <Button
                                  type="link"
                                  onPress={() => {
                                    openAddressInMaps(startAddress);
                                  }}
                                  disabled={isCarAvailableError}
                                  style={{
                                    paddingTop: 0,
                                    paddingRight: 0,
                                    paddingBottom: 0,
                                    paddingLeft: 0,
                                    alignItems: 'flex-start',
                                    justifyContent: 'flex-start',
                                  }}
                                >
                                  <Text
                                    type="bodyRegular"
                                    style={{
                                      textDecorationLine: 'underline',
                                      color: theme.colors.text.secondary,
                                    }}
                                  >
                                    {startAddress}
                                  </Text>
                                </Button>
                              ) : (
                                <View>
                                  <Text
                                    type="bodyRegular"
                                    style={{ color: theme.colors.error }}
                                  >
                                    {deliveryModeEnabled
                                      ? 'Enter delivery address'
                                      : 'Enter pickup address'}
                                  </Text>
                                </View>
                              )}
                            </View>
                          </View>

                          <View style={{ gap: 8, flexDirection: 'row' }}>
                            <View style={{ flex: 1 }}>
                              <Text tx="carId.returnLocation" type="bodyBold" />

                              <Text
                                type="bodyRegular"
                                style={{ color: theme.colors.text.secondary }}
                              >
                                {format(
                                  new Date(endDatetime),
                                  'MMM dd - hh:mm a'
                                )}
                              </Text>

                              {endAddress ? (
                                <Button
                                  type="link"
                                  onPress={() => {
                                    openAddressInMaps(endAddress);
                                  }}
                                  style={{
                                    paddingTop: 0,
                                    paddingRight: 0,
                                    paddingBottom: 0,
                                    paddingLeft: 0,
                                    alignItems: 'flex-start',
                                    justifyContent: 'flex-start',
                                  }}
                                >
                                  <Text
                                    type="bodyRegular"
                                    style={{
                                      textDecorationLine: 'underline',
                                      color: theme.colors.text.secondary,
                                    }}
                                  >
                                    {endAddress}
                                  </Text>
                                </Button>
                              ) : (
                                <View>
                                  <Text
                                    type="bodyRegular"
                                    style={{ color: theme.colors.error }}
                                  >
                                    Enter return address
                                  </Text>
                                </View>
                              )}
                            </View>
                          </View>
                        </View>
                      </Collapsible>

                      <View
                        style={{
                          backgroundColor: theme.colors.background_2,
                          borderColor: theme.colors.card.default.borderColor,
                          borderWidth: 1,
                          borderRadius: 16,
                        }}
                      >
                        <View
                          style={{
                            width: '100%',
                            marginTop: 16,
                            flexDirection: 'row',
                            width: '100%',
                            paddingLeft: 16,
                            paddingBottom: 16,
                            borderBottomColor:
                              theme.colors.card.default.borderColor,
                            borderBottomWidth: 1,
                          }}
                        >
                          <Text type="title2">Rental Breakdown</Text>
                        </View>

                        <View
                          style={{
                            padding: 16,
                            gap: 8,
                          }}
                        >
                          <View style={{ gap: 4 }}>
                            <View>
                              <Text type="bodyBold">{`${car.brandName} ${car.modelName} - ${car.modelType}`}</Text>
                            </View>

                            <View
                              style={{
                                flexDirection: 'row',
                                justifyContent: 'space-between',
                              }}
                            >
                              <View
                                style={{
                                  width: '33%',
                                  alignItems: 'flex-start',
                                }}
                              >
                                <Text
                                  type="bodyRegular"
                                  style={{ color: theme.colors.text.secondary }}
                                >
                                  {`$${car.dailyRate} / day`}
                                </Text>
                              </View>

                              <View
                                style={{
                                  width: '33%',
                                  alignItems: 'center',
                                }}
                              >
                                <Text
                                  type="bodyRegular"
                                  style={{ color: theme.colors.text.secondary }}
                                >
                                  {`x${carData.meta.numDays} days`}
                                </Text>
                              </View>

                              <View
                                style={{
                                  width: '33%',
                                  alignItems: 'flex-end',
                                }}
                              >
                                <Text
                                  type="bodyRegular"
                                  style={{ color: theme.colors.text.secondary }}
                                >
                                  {`$${formatNumber(
                                    car.dailyRate * carData.meta.numDays
                                  )}`}
                                </Text>
                              </View>
                            </View>
                          </View>

                          {car.applyMonthlyDiscount ? (
                            <View
                              style={{
                                flexDirection: 'row',
                                justifyContent: 'space-between',
                              }}
                            >
                              <Text
                                type="bodyRegular"
                                style={{ color: PINKLING }}
                              >
                                Monthly Discount
                              </Text>

                              <Text
                                type="bodyRegular"
                                style={{ color: PINKLING }}
                              >
                                {`-$${car.monthlyDiscountAmount}`}
                              </Text>
                            </View>
                          ) : null}

                          {car.applyWeeklyDiscount ? (
                            <View
                              style={{
                                flexDirection: 'row',
                                justifyContent: 'space-between',
                              }}
                            >
                              <Text
                                type="bodyRegular"
                                style={{ color: PINKLING }}
                              >
                                Weekly Discount
                              </Text>

                              <Text
                                type="bodyRegular"
                                style={{ color: PINKLING }}
                              >
                                {`-$${car.weeklyDiscountAmount}`}
                              </Text>
                            </View>
                          ) : null}

                          {insurance ? (
                            <View style={{ gap: 4 }}>
                              <View>
                                <Text type="bodyBold">{insurance.name}</Text>
                              </View>

                              <View
                                style={{
                                  width: '100%',
                                  flexDirection: 'row',
                                  justifyContent: 'space-between',
                                }}
                              >
                                <View
                                  style={{
                                    width: '33%',
                                    alignItems: 'flex-start',
                                  }}
                                >
                                  <Text
                                    type="bodyRegular"
                                    style={{
                                      color: theme.colors.text.secondary,
                                    }}
                                  >
                                    {isInsuranceFree(insurance)
                                      ? 'Free'
                                      : `$${formatNumber(
                                          getDailyInsuranceCost(
                                            insurance,
                                            car.dailyRate
                                          )
                                        )} / day`}
                                  </Text>
                                </View>

                                <View
                                  style={{
                                    width: '33%',
                                    alignItems: 'center',
                                  }}
                                >
                                  <Text
                                    type="bodyRegular"
                                    style={{
                                      color: theme.colors.text.secondary,
                                    }}
                                  >
                                    {`x${carData.meta.numDays} days`}
                                  </Text>
                                </View>

                                <View
                                  style={{
                                    width: '33%',
                                    alignItems: 'flex-end',
                                  }}
                                >
                                  <Text
                                    type="bodyRegular"
                                    style={{
                                      color: theme.colors.text.secondary,
                                    }}
                                  >
                                    {`$${formatNumber(
                                      getDailyInsuranceCost(
                                        insurance,
                                        car.dailyRate
                                      ) * carData.meta.numDays
                                    )}`}
                                  </Text>
                                </View>
                              </View>
                            </View>
                          ) : null}

                          {cancellationPolicy?.percentage &&
                          cancellationPolicy.percentage > 0 ? (
                            <View
                              style={{
                                flexDirection: 'row',
                                justifyContent: 'space-between',
                              }}
                            >
                              <Text type="bodyRegular">
                                {cancellationPolicy.title}
                              </Text>

                              <Text
                                type="bodyRegular"
                                style={{ color: theme.colors.text.secondary }}
                              >
                                {`$${formatNumber(
                                  (cancellationPolicy.percentage / 100) *
                                    car.dailyRate *
                                    carData.meta.numDays
                                )}`}
                              </Text>
                            </View>
                          ) : null}

                          {!isCarAvailableError && car.deliveryCost > 0 ? (
                            <View
                              style={{
                                flexDirection: 'row',
                                justifyContent: 'space-between',
                              }}
                            >
                              <Text type="bodyBold">Delivery Fee</Text>

                              <View>
                                <Text
                                  type="bodyRegular"
                                  style={{ color: theme.colors.text.secondary }}
                                >
                                  {`$${formatNumber(car.deliveryCost)}`}
                                </Text>
                              </View>
                            </View>
                          ) : null}

                          {discount ? (
                            <View
                              style={{
                                flexDirection: 'row',
                                justifyContent: 'space-between',
                              }}
                            >
                              <Text
                                type="bodyRegular"
                                style={{ color: PINKLING }}
                              >
                                {`Discount: ${discount.code}`}
                              </Text>

                              <Text
                                type="bodyRegular"
                                style={{ color: PINKLING }}
                              >
                                {`-$${formatNumber(discount.amount)}`}
                              </Text>
                            </View>
                          ) : null}
                        </View>

                        <View
                          style={{
                            flexDirection: 'row',
                            justifyContent: 'space-between',
                            padding: 16,
                          }}
                        >
                          <View>
                            <Text type="bodyBold">Total Rental Price</Text>
                          </View>

                          <View>
                            <Text type="bodyBold">{`$${formatNumber(
                              calculatedCost
                            )}`}</Text>
                          </View>
                        </View>

                        <View
                          style={{
                            paddingTop: 8,
                            paddingLeft: 16,
                            paddingRight: 16,
                            paddingBottom: 16,
                          }}
                        >
                          <Button
                            tx="carId.checkout"
                            type="primary"
                            onPress={handleCheckout}
                            disabled={!insurance || isCarAvailableError}
                          />
                        </View>
                      </View>
                    </View>
                  ) : null}
                </View>
              </View>
            </View>
          </ScrollView>

          {isSticky ? (
            <View
              style={{
                width: contentWidth * 0.35,
                height: '100%',
                position: 'absolute',
                top: navbarPosition.top + navbarPosition.height,
                left: stickyBarPosition.left,
                zIndex: 1000,
                gap: 24,
              }}
              // pointerEvents="none"
            >
              <Collapsible
                collapsed={collapseLocationDetails}
                onToggle={() => {
                  setCollapseLocationDetails(!collapseLocationDetails);
                }}
                title={t(
                  deliveryModeEnabled
                    ? 'carId.deliveryAndReturnDetails'
                    : 'carId.pickupAndReturnDetails'
                )}
              >
                <View style={{ padding: 16, gap: 24 }}>
                  <View style={{ gap: 8, flexDirection: 'row' }}>
                    <View style={{ flex: 1 }}>
                      <Text
                        tx={
                          deliveryModeEnabled
                            ? 'carId.deliveryLocation'
                            : 'carId.pickupLocation'
                        }
                        type="bodyBold"
                      />

                      <Text
                        type="bodyRegular"
                        style={{ color: theme.colors.text.secondary }}
                      >
                        {format(startDatetime, 'MMM dd - hh:mm a')}
                      </Text>

                      {startAddress ? (
                        <Button
                          type="link"
                          onPress={() => {
                            openAddressInMaps(startAddress);
                          }}
                          disabled={isCarAvailableError}
                          style={{
                            paddingTop: 0,
                            paddingRight: 0,
                            paddingBottom: 0,
                            paddingLeft: 0,
                            alignItems: 'flex-start',
                            justifyContent: 'flex-start',
                          }}
                        >
                          <Text
                            type="bodyRegular"
                            style={{
                              textDecorationLine: 'underline',
                              color: theme.colors.text.secondary,
                            }}
                          >
                            {startAddress}
                          </Text>
                        </Button>
                      ) : (
                        <View>
                          <Text
                            type="bodyRegular"
                            style={{ color: theme.colors.error }}
                          >
                            {deliveryModeEnabled
                              ? 'Enter delivery address'
                              : 'Enter pickup address'}
                          </Text>
                        </View>
                      )}
                    </View>
                  </View>

                  <View style={{ gap: 8, flexDirection: 'row' }}>
                    <View style={{ flex: 1 }}>
                      <Text tx="carId.returnLocation" type="bodyBold" />

                      <Text
                        type="bodyRegular"
                        style={{ color: theme.colors.text.secondary }}
                      >
                        {format(new Date(endDatetime), 'MMM dd - hh:mm a')}
                      </Text>

                      {endAddress ? (
                        <Button
                          type="link"
                          onPress={() => {
                            openAddressInMaps(endAddress);
                          }}
                          style={{
                            paddingTop: 0,
                            paddingRight: 0,
                            paddingBottom: 0,
                            paddingLeft: 0,
                            alignItems: 'flex-start',
                            justifyContent: 'flex-start',
                          }}
                        >
                          <Text
                            type="bodyRegular"
                            style={{
                              textDecorationLine: 'underline',
                              color: theme.colors.text.secondary,
                            }}
                          >
                            {endAddress}
                          </Text>
                        </Button>
                      ) : (
                        <View>
                          <Text
                            type="bodyRegular"
                            style={{ color: theme.colors.error }}
                          >
                            Enter return address
                          </Text>
                        </View>
                      )}
                    </View>
                  </View>
                </View>
              </Collapsible>

              <View
                style={{
                  backgroundColor: theme.colors.background_2,
                  borderColor: theme.colors.card.default.borderColor,
                  borderWidth: 1,
                  borderRadius: 16,
                }}
              >
                <View
                  style={{
                    width: '100%',
                    marginTop: 16,
                    flexDirection: 'row',
                    width: '100%',
                    paddingLeft: 16,
                    paddingBottom: 16,
                    borderBottomColor: theme.colors.card.default.borderColor,
                    borderBottomWidth: 1,
                  }}
                >
                  <Text type="title2">Rental Breakdown</Text>
                </View>

                <View
                  style={{
                    padding: 16,
                    gap: 8,
                  }}
                >
                  <View style={{ gap: 4 }}>
                    <View>
                      <Text type="bodyBold">{`${car.brandName} ${car.modelName} - ${car.modelType}`}</Text>
                    </View>

                    <View
                      style={{
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                      }}
                    >
                      <View
                        style={{
                          width: '33%',
                          alignItems: 'flex-start',
                        }}
                      >
                        <Text
                          type="bodyRegular"
                          style={{ color: theme.colors.text.secondary }}
                        >
                          {`$${car.dailyRate} / day`}
                        </Text>
                      </View>

                      <View
                        style={{
                          width: '33%',
                          alignItems: 'center',
                        }}
                      >
                        <Text
                          type="bodyRegular"
                          style={{ color: theme.colors.text.secondary }}
                        >
                          {`x${carData.meta.numDays} days`}
                        </Text>
                      </View>

                      <View
                        style={{
                          width: '33%',
                          alignItems: 'flex-end',
                        }}
                      >
                        <Text
                          type="bodyRegular"
                          style={{ color: theme.colors.text.secondary }}
                        >
                          {`$${formatNumber(
                            car.dailyRate * carData.meta.numDays
                          )}`}
                        </Text>
                      </View>
                    </View>
                  </View>

                  {car.applyMonthlyDiscount ? (
                    <View
                      style={{
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Text type="bodyRegular" style={{ color: PINKLING }}>
                        Monthly Discount
                      </Text>

                      <Text type="bodyRegular" style={{ color: PINKLING }}>
                        {`-$${car.monthlyDiscountAmount}`}
                      </Text>
                    </View>
                  ) : null}

                  {car.applyWeeklyDiscount ? (
                    <View
                      style={{
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Text type="bodyRegular" style={{ color: PINKLING }}>
                        Weekly Discount
                      </Text>

                      <Text type="bodyRegular" style={{ color: PINKLING }}>
                        {`-$${car.weeklyDiscountAmount}`}
                      </Text>
                    </View>
                  ) : null}

                  {insurance ? (
                    <View style={{ gap: 4 }}>
                      <View>
                        <Text type="bodyBold">{insurance.name}</Text>
                      </View>

                      <View
                        style={{
                          width: '100%',
                          flexDirection: 'row',
                          justifyContent: 'space-between',
                        }}
                      >
                        <View
                          style={{
                            width: '33%',
                            alignItems: 'flex-start',
                          }}
                        >
                          <Text
                            type="bodyRegular"
                            style={{ color: theme.colors.text.secondary }}
                          >
                            {isInsuranceFree(insurance)
                              ? 'Free'
                              : `$${formatNumber(
                                  getDailyInsuranceCost(
                                    insurance,
                                    car.dailyRate
                                  )
                                )} / day`}
                          </Text>
                        </View>

                        <View
                          style={{
                            width: '33%',
                            alignItems: 'center',
                          }}
                        >
                          <Text
                            type="bodyRegular"
                            style={{ color: theme.colors.text.secondary }}
                          >
                            {`x${carData.meta.numDays} days`}
                          </Text>
                        </View>

                        <View
                          style={{
                            width: '33%',
                            alignItems: 'flex-end',
                          }}
                        >
                          <Text
                            type="bodyRegular"
                            style={{ color: theme.colors.text.secondary }}
                          >
                            {`$${formatNumber(
                              getDailyInsuranceCost(insurance, car.dailyRate) *
                                carData.meta.numDays
                            )}`}
                          </Text>
                        </View>
                      </View>
                    </View>
                  ) : null}

                  {cancellationPolicy?.percentage &&
                  cancellationPolicy.percentage > 0 ? (
                    <View
                      style={{
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Text type="bodyRegular">{cancellationPolicy.title}</Text>

                      <Text
                        type="bodyRegular"
                        style={{ color: theme.colors.text.secondary }}
                      >
                        {`$${formatNumber(
                          (cancellationPolicy.percentage / 100) *
                            car.dailyRate *
                            carData.meta.numDays
                        )}`}
                      </Text>
                    </View>
                  ) : null}

                  {!isCarAvailableError && car.deliveryCost > 0 ? (
                    <View
                      style={{
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Text type="bodyBold">Delivery Fee</Text>

                      <View>
                        <Text
                          type="bodyRegular"
                          style={{ color: theme.colors.text.secondary }}
                        >
                          {`$${formatNumber(car.deliveryCost)}`}
                        </Text>
                      </View>
                    </View>
                  ) : null}

                  {discount ? (
                    <View
                      style={{
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Text type="bodyRegular" style={{ color: PINKLING }}>
                        {`Discount: ${discount.code}`}
                      </Text>

                      <Text type="bodyRegular" style={{ color: PINKLING }}>
                        {`-$${formatNumber(discount.amount)}`}
                      </Text>
                    </View>
                  ) : null}
                </View>

                <View
                  style={{
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    padding: 16,
                  }}
                >
                  <View>
                    <Text type="bodyBold">Total Rental Price</Text>
                  </View>

                  <View>
                    <Text type="bodyBold">{`$${formatNumber(
                      calculatedCost
                    )}`}</Text>
                  </View>
                </View>

                <View
                  style={{
                    paddingTop: 8,
                    paddingLeft: 16,
                    paddingRight: 16,
                    paddingBottom: 16,
                  }}
                >
                  <Button
                    tx="carId.checkout"
                    type="primary"
                    onPress={handleCheckout}
                    disabled={!insurance || isCarAvailableError}
                    pointerEvents="auto"
                  />
                </View>
              </View>
            </View>
          ) : null}
        </View>

        <CompareCancellationPoliciesModal
          visible={showCompareCancellationPoliciesModal}
          onClose={() => {
            setShowCompareCancellationPoliciesModal(false);
          }}
          cancellationPolicies={addOns?.cancellationOptions ?? []}
        />

        <ConfirmModal
          isVisible={isCarAvailableData && !isCarAvailableData.isAvailable}
          titleTx="carId.carNotAvailable"
          body={
            <View style={{ paddingLeft: 8, paddingRight: 16, gap: 16 }}>
              <View style={{ gap: 8 }}>
                <Text type="bodyRegular" tx="carId.carNotAvailableBody" />
              </View>
            </View>
          }
          rightButton={{
            tx: 'carId.backToSearch',
            onPress: () => {
              // TODO: redirect for web
              router.canGoBack() ? router.back() : router.navigate('/');
            },
          }}
          onDismiss={() => {
            // TODO: redirect for web
            router.canGoBack() ? router.back() : router.navigate('/');
          }}
          dismissable={false}
        />

        <ConfirmModal
          isVisible={showNoInsuranceModal}
          titleTx="carId.ownInsuranceTitle"
          body={
            <View style={{ paddingLeft: 8, paddingRight: 16, gap: 16 }}>
              <View style={{ gap: 8 }}>
                <Text type="bodyBold">Use your own coverage.</Text>

                <Text type="bodyRegular">
                  We'll need to verify your insurance after your booking is
                  made.
                </Text>
              </View>

              <View
                style={{
                  flexDirection: 'row',
                  gap: 8,
                  alignItems: 'center',
                }}
              >
                <Icon
                  type="naked"
                  icon={<Cancel />}
                  stroke={theme.colors.input.error.helperText}
                  fillColor={theme.colors.input.error.helperText}
                  width={12}
                  height={12}
                />

                <Text
                  style={{
                    color: theme.colors.text.secondary,
                  }}
                  type="bodyRegular"
                >
                  Credit cards do not cover liability insurance.
                </Text>
              </View>

              <View
                style={{
                  flexDirection: 'row',
                  gap: 8,
                }}
              >
                <View style={{ marginTop: 8 }}>
                  <Icon
                    type="naked"
                    icon={<Cancel />}
                    stroke={theme.colors.input.error.helperText}
                    fillColor={theme.colors.input.error.helperText}
                    width={12}
                    height={12}
                  />
                </View>

                <Text
                  style={{
                    color: theme.colors.text.secondary,
                  }}
                  type="bodyRegular"
                >
                  Driving without liability insurance is illegal and can result
                  in severe penalties.
                </Text>
              </View>

              <View
                style={{
                  flexDirection: 'row',
                  gap: 8,
                }}
              >
                <View style={{ marginTop: 8 }}>
                  <Icon
                    type="naked"
                    icon={<Exclamations />}
                    fillColor={theme.colors.icon.color}
                    width={12}
                    height={12}
                  />
                </View>

                <Text
                  style={{
                    color: theme.colors.text.secondary,
                  }}
                  type="bodyRegular"
                >
                  You are personally responsible for any damages caused in an
                  accident, which can lead to substantial financial losses.
                </Text>
              </View>
            </View>
          }
          leftButton={{
            tx: 'carId.ownInsuranceKeep',
            onPress: () => {
              setShowNoInsuranceModal(false);
            },
          }}
          rightButton={{
            tx: 'carId.ownInsuranceUpgrade',
            onPress: () => {
              const minInsurance = addOns?.insuranceOptions.find(
                (option) => option.name === 'Minimum Protection'
              );

              setInsurance(minInsurance);

              setShowNoInsuranceModal(false);
            },
          }}
          onDismiss={() => {
            setShowNoInsuranceModal(false);
          }}
        />

        <DeliveryFeeModal
          visible={showHowDeliveryFeesModal}
          onClose={() => {
            setShowHowDeliveryFeesModal(false);
          }}
        />

        <FinishProfileModal
          visible={showFinishProfileModal}
          onClose={() => setShowFinishProfileModal(false)}
          onDone={() => setShowFinishProfileModal(false)}
        />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <Stack.Screen
        options={{
          headerTitle: '',
          headerLeft: () => (
            <BackButton
              onPress={() => {
                router.navigate({
                  pathname: '/search',
                  params: {
                    start_datetime: format(
                      startDatetime,
                      'yyyy-MM-dd HH:mm:ss'
                    ),
                    end_datetime: format(endDatetime, 'yyyy-MM-dd HH:mm:ss'),
                    delivery_address:
                      searchDeliveryDeliverToAddress?.description,
                    delivery_address_lat:
                      searchDeliveryDeliverToAddress?.latitude,
                    delivery_address_lng:
                      searchDeliveryDeliverToAddress?.longitude,
                    return_address: searchDeliveryReturnAddress?.description,
                    return_address_lat: searchDeliveryReturnAddress?.latitude,
                    return_address_lng: searchDeliveryReturnAddress?.longitude,
                    mode: searchDeliveryModeEnabled ? 'delivery' : 'pickup',
                    code: discountCode,
                  },
                });
              }}
            />
          ),
          headerStyle: {
            backgroundColor: theme.colors.background_1,
          },
          headerTintColor: theme.colors.text.primary,
          headerShadowVisible: false,
        }}
      />

      <View style={styles.scrollViewContainer}>
        <KeyboardAwareScrollView
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
          extraHeight={Platform.OS === 'ios' ? 200 : 100}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="always"
          enableOnAndroid
        >
          <View style={{ flexGrow: 1, gap: 32 }}>
            <View
              style={{
                width: '100%',
                height: 200,
              }}
              id="carousel-component"
              dataSet={{ kind: 'basic-layouts', name: 'parallax' }}
            >
              <Carousel
                ref={carouselRef}
                width={PAGE_WIDTH}
                style={{
                  width: PAGE_WIDTH,
                }}
                height={200}
                data={getCarImages(car.images, 'small')}
                mode="parallax"
                modeConfig={{
                  parallaxScrollingScale: 1,
                  parallaxScrollingOffset: 0,
                }}
                loop={false}
                onProgressChange={progress}
                renderItem={({ item }) => {
                  return (
                    <Image
                      source={{ uri: item.url }}
                      style={{
                        width: '90%',
                        aspectRatio: 16 / 9,
                        borderRadius: 16,
                      }}
                      contentFit="cover"
                    />
                  );
                }}
              />

              <Pagination.Basic
                progress={progress}
                data={getCarImages(car.images)}
                dotStyle={{
                  backgroundColor: theme.colors.paginationDots.empty,
                  borderRadius: 50,
                }}
                activeDotStyle={{
                  backgroundColor: theme.colors.paginationDots.filled,
                }}
                size={8}
                containerStyle={{ gap: 10, marginTop: 10 }}
                onPress={onPressPagination}
              />
            </View>

            <View style={{ gap: 16 }}>
              <View style={{ flexDirection: 'row', gap: 8 }}>
                <View
                  style={{
                    backgroundColor: theme.colors.icon.fill,
                    padding: 10,
                    borderRadius: 8,
                    width: 40,
                    height: 40,
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <Image
                    style={{
                      width: 26,
                      height: 26,
                    }}
                    source={getCarBrandLogo(car.brandLogos)}
                    contentFit="cover"
                  />
                </View>

                <View>
                  <Text type="title1">{`${car.brandName} ${car.modelName}`}</Text>
                  <Text
                    type="bodyRegular"
                    style={{ color: theme.colors.text.secondary }}
                  >
                    {car.modelType}
                  </Text>
                </View>
              </View>

              <View style={{ gap: 8 }}>
                <View style={{ flexDirection: 'row', gap: 8 }}>
                  <Chip icon={<SteeringWheel />} text={car.autopilotType} />

                  <Chip
                    icon={<Battery />}
                    text={`${car.batteryRange} Miles Per Charge`}
                  />
                </View>

                <View style={{ flexDirection: 'row', gap: 8 }}>
                  <Chip icon={<Seat />} text={`${car.numSeats} Seats`} />

                  <Chip
                    imgSrc={colorImgSrc}
                    useDefaultIconFillColor={false}
                    text="Exterior"
                  />
                </View>
              </View>

              <View>
                <ShowMore text={car.description} />
              </View>
            </View>

            <View
              style={{
                width: '100%',
                backgroundColor: theme.colors.card.default.borderColor,
                height: 1,
              }}
            />

            <View>
              <Card>
                <View
                  style={{
                    padding: 16,
                    borderBottomColor: theme.colors.card.default.borderColor,
                    borderBottomWidth: 1,
                  }}
                >
                  <Text
                    tx={
                      deliveryModeEnabled &&
                      deliveryDeliverToAddress &&
                      deliveryReturnAddress
                        ? 'carId.deliveryAndReturnDetails'
                        : 'carId.pickupAndReturnDetails'
                    }
                    type="title2"
                  />
                </View>

                <View style={{ padding: 16, gap: 24 }}>
                  <View style={{ gap: 8, flexDirection: 'row' }}>
                    <View style={{ flex: 1 }}>
                      <Text
                        tx={
                          deliveryModeEnabled &&
                          deliveryDeliverToAddress &&
                          deliveryReturnAddress
                            ? 'carId.deliveryLocation'
                            : 'carId.pickupLocation'
                        }
                        type="bodyBold"
                      />

                      <Text
                        type="bodyRegular"
                        style={{ color: theme.colors.text.secondary }}
                      >
                        {format(startDatetime, 'MMM dd - hh:mm a')}
                      </Text>

                      {startAddress ? (
                        <Button
                          type="link"
                          onPress={() => {
                            openAddressInMaps(startAddress);
                          }}
                          disabled={isCarAvailableError}
                          style={{
                            paddingTop: 0,
                            paddingRight: 0,
                            paddingBottom: 0,
                            paddingLeft: 0,
                            alignItems: 'flex-start',
                            justifyContent: 'flex-start',
                          }}
                        >
                          <Text
                            type="bodyRegular"
                            style={{
                              textDecorationLine: 'underline',
                              color: theme.colors.text.secondary,
                            }}
                          >
                            {startAddress}
                          </Text>
                        </Button>
                      ) : (
                        <View>
                          <Text>
                            {deliveryModeEnabled
                              ? 'Enter delivery address'
                              : 'Enter pickup address'}
                          </Text>
                        </View>
                      )}
                    </View>
                  </View>

                  <View
                    style={{
                      gap: 4,
                      flexDirection: 'row',
                    }}
                  >
                    <View style={{ flex: 1 }}>
                      <Text tx="carId.returnLocation" type="bodyBold" />

                      <Text
                        type="bodyRegular"
                        style={{ color: theme.colors.text.secondary }}
                      >
                        {format(new Date(endDatetime), 'MMM dd - hh:mm a')}
                      </Text>

                      {endAddress ? (
                        <Button
                          type="link"
                          onPress={() => {
                            openAddressInMaps(endAddress);
                          }}
                          style={{
                            paddingTop: 0,
                            paddingRight: 0,
                            paddingBottom: 0,
                            paddingLeft: 0,
                            alignItems: 'flex-start',
                            justifyContent: 'flex-start',
                          }}
                        >
                          <Text
                            type="bodyRegular"
                            style={{
                              textDecorationLine: 'underline',
                              color: theme.colors.text.secondary,
                            }}
                          >
                            {endAddress}
                          </Text>
                        </Button>
                      ) : (
                        <View>
                          <Text>Enter return address</Text>
                        </View>
                      )}
                    </View>
                  </View>
                </View>
              </Card>
            </View>

            <View style={{ flex: 1, gap: 16 }}>
              <Text type="title2" tx="carId.carLocation" />

              <Button
                type="link"
                onPress={() => {
                  openAddressInMaps(car.defaultLocation.address);
                }}
                textStyle={{ fontSize: 12 }}
                style={{
                  height: 20,
                  paddingTop: 0,
                  paddingRight: 0,
                  paddingBottom: 0,
                  paddingLeft: 0,
                  alignItems: 'flex-start',
                  justifyContent: 'flex-start',
                }}
              >
                <Text
                  style={{
                    textDecorationLine: 'underline',
                    color: theme.colors.text.secondary,
                  }}
                  type="bodyRegular"
                >
                  {car.defaultLocation.address}
                </Text>
              </Button>
            </View>

            <View
              style={{
                width: '100%',
                height: 300,
                borderRadius: 16,
                position: 'relative',
                zIndex: 1,
                overflow: 'hidden',
              }}
            >
              <MapView
                polylines={[]}
                markers={markers}
                defaultCenter={defaultCenter}
                containerStyle={{ borderRadius: 16 }}
                scrollEnabled={false}
                zoomEnabled={false}
                fitBounds={Boolean(
                  deliveryModeEnabled &&
                    deliveryDeliverToAddress &&
                    deliveryReturnAddress
                )}
                altitude={3000}
                zoom={16}
                showsUserLocation={false}
              />
            </View>

            {car.supportsDelivery ? (
              <View style={{ gap: 16 }}>
                <CardItem
                  key="pickupAndReturn"
                  icon={
                    <Icon
                      type="dressed"
                      icon={<Pin />}
                      fillColor={theme.colors.icon.color}
                      width={24}
                      height={24}
                    />
                  }
                  title={t('carId.pickupAndReturnDetails')}
                  body={t('carId.pickupAndReturnBody')}
                  style={{ width: '100%' }}
                  active={!deliveryModeEnabled}
                  onPress={() => {
                    setDeliveryModeEnabled(false);
                  }}
                />

                <CardItem
                  key="delivery"
                  icon={
                    <Icon
                      type="dressed"
                      icon={<Location />}
                      fillColor={theme.colors.icon.color}
                      width={24}
                      height={24}
                    />
                  }
                  title={t('carId.delivery')}
                  body={t('carId.deliveryBody')}
                  style={{ width: '100%' }}
                  active={deliveryModeEnabled}
                  onPress={() => {
                    setDeliveryModeEnabled(true);
                  }}
                />
              </View>
            ) : null}

            {deliveryModeEnabled ? (
              <View
                style={{
                  borderRadius: 16,
                  flexDirection: 'column',
                  gap: 24,
                  zIndex: 1,
                }}
              >
                <View style={{ gap: 8, zIndex: 2 }}>
                  {isCarAvailableError ? (
                    <View>
                      <Text style={{ color: 'red' }}>
                        {carAvailableError.message}
                      </Text>
                    </View>
                  ) : null}

                  {loadedGoogle ? (
                    <View style={{ flexGrow: 1, width: '100%' }}>
                      <GoogleAutocomplete
                        label="Delivery Address"
                        placeholder="Delivery address"
                        onPress={(place: Address) => {
                          setDeliveryDeliverToAddress(place);

                          if (checkedReturnAddress) {
                            setDeliveryReturnToAddress(place);
                          }
                        }}
                        inputStyle={{ width: 300 }}
                        address={deliveryDeliverToAddress}
                        onClear={() => {
                          setDeliveryDeliverToAddress(null);
                        }}
                        autofocus={false}
                      />
                    </View>
                  ) : null}
                </View>

                <View style={{ gap: 8, zIndex: 1 }}>
                  {loadedGoogle ? (
                    <View style={{ flexGrow: 1, width: '100%' }}>
                      <GoogleAutocomplete
                        label="Return Address"
                        placeholder="Return address"
                        onPress={(place: Address) => {
                          setDeliveryReturnToAddress(place);
                        }}
                        inputStyle={{ width: 300 }}
                        address={deliveryReturnAddress}
                        disabled={checkedReturnAddress}
                        onClear={() => {
                          setDeliveryReturnToAddress(null);
                        }}
                        autofocus={false}
                      />
                    </View>
                  ) : null}
                </View>

                {deliveryDeliverToAddress ? (
                  <View>
                    <CheckItem
                      text="Return address is the same address"
                      onPress={() => {
                        if (checkedReturnAddress) {
                          setDeliveryReturnToAddress(null);
                        } else {
                          setDeliveryReturnToAddress(deliveryDeliverToAddress);
                        }

                        setCheckedReturnAddress(!checkedReturnAddress);
                      }}
                      active={checkedReturnAddress}
                      type="checkbox"
                    />
                  </View>
                ) : null}

                {!isCarAvailableError ? (
                  <Card
                    style={{
                      paddingLeft: 16,
                      paddingRight: 16,
                      paddingTop: 24,
                      paddingBottom: 24,
                    }}
                  >
                    <Pressable
                      hitSlop={8}
                      onPress={() => {
                        setShowHowDeliveryFeesModal(true);
                      }}
                    >
                      <View
                        style={{
                          flexDirection: 'row',
                          justifyContent: 'space-between',
                        }}
                      >
                        <View
                          style={{
                            flexDirection: 'row',
                            gap: 8,
                            alignItems: 'center',
                          }}
                        >
                          <Text type="bodyRegular" tx="carId.deliveryFee" />

                          <Icon
                            type="naked"
                            icon={<Info />}
                            fillColor={theme.colors.icon.color}
                            width={15}
                            height={15}
                          />
                        </View>

                        <View>
                          <Text type="bodyBold">{`+$${formatNumber(
                            car.deliveryCost
                          )}`}</Text>
                        </View>
                      </View>
                    </Pressable>
                  </Card>
                ) : null}
              </View>
            ) : null}

            <View
              style={{
                width: '100%',
                backgroundColor: theme.colors.card.default.borderColor,
                height: 1,
              }}
            />

            <View style={{ gap: 24 }}>
              <Text tx="carId.insurancePlans" type="title2" />

              <View style={{ gap: 16 }}>
                {addOns?.insuranceOptions.map((option) => {
                  return (
                    <InsuranceCardItem
                      key={option.name}
                      insurance={option}
                      car={car}
                      style={{ width: '100%' }}
                      active={insurance?.id === option.id}
                      onPress={() => handleInsuranceSelect(option)}
                    />
                  );
                })}
              </View>
            </View>

            {showCancellationOptions ? (
              <>
                <View
                  style={{
                    width: '100%',
                    backgroundColor: theme.colors.card.default.borderColor,
                    height: 1,
                  }}
                />
                <CancellationOptionsSection
                  options={addOns?.cancellationOptions}
                  selectedId={cancellationPolicy?.id}
                  onSelect={handleCancellationPolicySelect}
                  onCompare={() => {
                    setShowCompareCancellationPoliciesModal(true);
                  }}
                  car={car}
                />
              </>
            ) : null}

            <View
              style={{
                width: '100%',
                backgroundColor: theme.colors.card.default.borderColor,
                height: 1,
              }}
            />

            <View style={{ paddingBottom: 24 }}>
              <Discount
                title={t('discount.addDiscountCode')}
                inputLabel={t('discount.enterDiscountCodeLabel')}
                buttonTx={'discount.redeemCode'}
                onChangeText={(text: string) => {
                  setDiscountCode(text);
                }}
                onPress={() => {}}
                onSuccess={(d: DiscountType) => {
                  setDiscount(d);
                }}
                disabled={discountCode.length === 0}
                defaultText={local.code}
              />
            </View>
          </View>
        </KeyboardAwareScrollView>
      </View>

      <View style={styles.overviewContainer}>
        <View
          style={{
            flexGrow: 1,
            paddingLeft: 24,
            paddingRight: 24,
            paddingTop: 16,
            flexDirection: 'row',
            justifyContent: 'space-between',
          }}
        >
          <View>
            <View
              style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}
            >
              <View style={{ flexDirection: 'row', gap: 4 }}>
                {car.discountedDailyRate ? (
                  <Text
                    type="bodyRegular"
                    style={{ textDecorationLine: 'line-through' }}
                  >
                    {`$${car.dailyRate}`}
                  </Text>
                ) : null}

                <Text type="bodyBold">{`$${
                  formatNumber(car.discountedDailyRate) ||
                  formatNumber(car.dailyRate)
                } / day`}</Text>
              </View>

              <Pressable
                onPress={() => {
                  setShowRentalBreakdownModal(true);
                }}
                hitSlop={8}
              >
                <Icon
                  type="naked"
                  icon={<Info />}
                  fillColor={theme.colors.icon.color}
                  width={15}
                  height={15}
                />
              </Pressable>
            </View>

            <View>
              <View style={{ alignSelf: 'flex-start' }}>
                <Text>{`Total $${formatNumber(calculatedCost)}`}</Text>
              </View>
            </View>
          </View>

          <View>
            <Button
              tx="carId.checkout"
              type="primary"
              onPress={handleCheckout}
              disabled={!insurance || isCarAvailableError}
              style={{
                width: 130,
              }}
            />
          </View>
        </View>
      </View>

      <Modal
        animationIn="slideInUp"
        animationInTiming={300}
        animationOut="slideOutDown"
        animationOutTiming={300}
        isVisible={showRentalBreakdownModal}
        hasBackdrop
        style={{
          justifyContent: 'flex-end',
          margin: 0,
        }}
        onBackdropPress={() => {
          setShowRentalBreakdownModal(false);
        }}
      >
        <View
          style={{
            backgroundColor: theme.colors.background_2,
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            paddingBottom: 24,
          }}
        >
          <View
            style={{
              width: '100%',
              alignSelf: 'center',
              marginTop: 16,
              flexDirection: 'row',
              width: '100%',
              justifyContent: 'center',
              paddingLeft: 16,
              paddingBottom: 16,
              marginRight: 16,
              borderBottomColor: theme.colors.card.default.borderColor,
              borderBottomWidth: 1,
            }}
          >
            <Text type="title2">Rental Breakdown</Text>

            <NavigationButton
              type={NavigationButtonTypes.cancel}
              onPress={() => {
                setShowRentalBreakdownModal(false);
              }}
              style={{
                position: 'absolute',
                right: 16,
              }}
            />
          </View>

          <View
            style={{
              padding: 16,
              gap: 8,
              borderBottomColor: theme.colors.card.default.borderColor,
              borderBottomWidth: 1,
            }}
          >
            <View style={{ gap: 4 }}>
              <View>
                <Text type="bodyBold">{`${car.brandName} ${car.modelName} - ${car.modelType}`}</Text>
              </View>

              <View
                style={{
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                }}
              >
                <View
                  style={{
                    width: '33%',
                    alignItems: 'flex-start',
                  }}
                >
                  <Text
                    type="bodyRegular"
                    style={{ color: theme.colors.text.secondary }}
                  >
                    {`$${car.dailyRate} / day`}
                  </Text>
                </View>

                <View
                  style={{
                    width: '33%',
                    alignItems: 'center',
                  }}
                >
                  <Text
                    type="bodyRegular"
                    style={{ color: theme.colors.text.secondary }}
                  >
                    {`x${carData.meta.numDays} days`}
                  </Text>
                </View>

                <View
                  style={{
                    width: '33%',
                    alignItems: 'flex-end',
                  }}
                >
                  <Text
                    type="bodyRegular"
                    style={{ color: theme.colors.text.secondary }}
                  >
                    {`$${formatNumber(car.dailyRate * carData.meta.numDays)}`}
                  </Text>
                </View>
              </View>
            </View>

            {car.applyMonthlyDiscount ? (
              <View
                style={{
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                }}
              >
                <Text type="bodyRegular" style={{ color: PINKLING }}>
                  Monthly Discount
                </Text>

                <Text type="bodyRegular" style={{ color: PINKLING }}>
                  {`-$${car.monthlyDiscountAmount}`}
                </Text>
              </View>
            ) : null}

            {car.applyWeeklyDiscount ? (
              <View
                style={{
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                }}
              >
                <Text type="bodyRegular" style={{ color: PINKLING }}>
                  Weekly Discount
                </Text>

                <Text type="bodyRegular" style={{ color: PINKLING }}>
                  {`-$${car.weeklyDiscountAmount}`}
                </Text>
              </View>
            ) : null}

            {insurance ? (
              <View style={{ gap: 4 }}>
                <View>
                  <Text type="bodyBold">{insurance.name}</Text>
                </View>

                <View
                  style={{
                    width: '100%',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                  }}
                >
                  <View
                    style={{
                      width: '33%',
                      alignItems: 'flex-start',
                    }}
                  >
                    <Text
                      type="bodyRegular"
                      style={{ color: theme.colors.text.secondary }}
                    >
                      {isInsuranceFree(insurance)
                        ? 'Free'
                        : `$${formatNumber(
                            getDailyInsuranceCost(insurance, car.dailyRate)
                          )} / day`}
                    </Text>
                  </View>

                  <View
                    style={{
                      width: '33%',
                      alignItems: 'center',
                    }}
                  >
                    <Text
                      type="bodyRegular"
                      style={{ color: theme.colors.text.secondary }}
                    >
                      {`x${carData.meta.numDays} days`}
                    </Text>
                  </View>

                  <View
                    style={{
                      width: '33%',
                      alignItems: 'flex-end',
                    }}
                  >
                    <Text
                      type="bodyRegular"
                      style={{ color: theme.colors.text.secondary }}
                    >
                      {`$${formatNumber(
                        getDailyInsuranceCost(insurance, car.dailyRate) *
                          carData.meta.numDays
                      )}`}
                    </Text>
                  </View>
                </View>
              </View>
            ) : null}

            {cancellationPolicy?.percentage &&
            cancellationPolicy.percentage > 0 ? (
              <View
                style={{
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                }}
              >
                <Text type="bodyRegular">{cancellationPolicy.title}</Text>

                <Text
                  type="bodyRegular"
                  style={{ color: theme.colors.text.secondary }}
                >
                  {`$${formatNumber(
                    (cancellationPolicy.percentage / 100) *
                      car.dailyRate *
                      carData.meta.numDays
                  )}`}
                </Text>
              </View>
            ) : null}

            {!isCarAvailableError && car.deliveryCost > 0 ? (
              <View
                style={{
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                }}
              >
                <Text type="bodyBold">Delivery Fee</Text>

                <View>
                  <Text
                    type="bodyRegular"
                    style={{ color: theme.colors.text.secondary }}
                  >
                    {`$${formatNumber(car.deliveryCost)}`}
                  </Text>
                </View>
              </View>
            ) : null}

            {discount ? (
              <View
                style={{
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                }}
              >
                <Text type="bodyRegular" style={{ color: PINKLING }}>
                  {`Discount: ${discount.code}`}
                </Text>

                <Text type="bodyRegular" style={{ color: PINKLING }}>
                  {`-$${formatNumber(discount.amount)}`}
                </Text>
              </View>
            ) : null}
          </View>

          <View
            style={{
              flexDirection: 'row',
              justifyContent: 'space-between',
              padding: 16,
            }}
          >
            <View>
              <Text type="bodyBold">Total Rental Price</Text>
            </View>

            <View>
              <Text type="bodyBold">{`$${formatNumber(calculatedCost)}`}</Text>
            </View>
          </View>
        </View>
      </Modal>

      <CompareCancellationPoliciesModal
        visible={showCompareCancellationPoliciesModal}
        onClose={() => {
          setShowCompareCancellationPoliciesModal(false);
        }}
        cancellationPolicies={addOns?.cancellationOptions ?? []}
      />

      <ConfirmModal
        isVisible={isCarAvailableData && !isCarAvailableData.isAvailable}
        titleTx="carId.carNotAvailable"
        body={
          <View style={{ paddingLeft: 8, paddingRight: 16, gap: 16 }}>
            <View style={{ gap: 8 }}>
              <Text type="bodyRegular" tx="carId.carNotAvailableBody" />
            </View>
          </View>
        }
        rightButton={{
          tx: 'carId.backToSearch',
          onPress: () => {
            // TODO: redirect for web
            router.canGoBack() ? router.back() : router.navigate('/');
          },
        }}
        onDismiss={() => {
          // TODO: redirect for web
          router.canGoBack() ? router.back() : router.navigate('/');
        }}
        dismissable={false}
      />

      <ConfirmModal
        isVisible={showNoInsuranceModal}
        titleTx="carId.ownInsuranceTitle"
        body={
          <View style={{ paddingLeft: 8, paddingRight: 16, gap: 16 }}>
            <View style={{ gap: 8 }}>
              <Text type="bodyBold">Use your own coverage.</Text>

              <Text type="bodyRegular">
                We'll need to verify your insurance after your booking is made.
              </Text>
            </View>

            <View
              style={{
                flexDirection: 'row',
                gap: 8,
              }}
            >
              <View>
                <Icon
                  type="naked"
                  icon={<Cancel />}
                  fillColor={theme.colors.input.error.helperText}
                  stroke={theme.colors.input.error.helperText}
                  width={12}
                  height={12}
                />
              </View>

              <Text
                style={{
                  color: theme.colors.text.secondary,
                }}
                type="bodyRegular"
              >
                Credit cards do not cover liability insurance.
              </Text>
            </View>

            <View
              style={{
                flexDirection: 'row',
                gap: 8,
              }}
            >
              <View>
                <Icon
                  type="naked"
                  icon={<Cancel />}
                  fillColor={theme.colors.input.error.helperText}
                  stroke={theme.colors.input.error.helperText}
                  width={12}
                  height={12}
                />
              </View>

              <Text
                style={{
                  color: theme.colors.text.secondary,
                }}
                type="bodyRegular"
              >
                Driving without liability insurance is illegal and can result in
                severe penalties.
              </Text>
            </View>

            <View
              style={{
                flexDirection: 'row',
                gap: 8,
              }}
            >
              <View>
                <Icon
                  type="naked"
                  icon={<Exclamations />}
                  fillColor={theme.colors.icon.color}
                  width={12}
                  height={12}
                />
              </View>

              <Text
                style={{
                  color: theme.colors.text.secondary,
                }}
                type="bodyRegular"
              >
                You are personally responsible for any damages caused in an
                accident, which can lead to substantial financial losses.
              </Text>
            </View>
          </View>
        }
        leftButton={{
          tx: 'carId.ownInsuranceKeep',
          onPress: () => {
            setShowNoInsuranceModal(false);
          },
        }}
        rightButton={{
          tx: 'carId.ownInsuranceUpgrade',
          onPress: () => {
            const minInsurance = addOns?.insuranceOptions.find(
              (option) => option.name === 'Minimum Protection'
            );

            setInsurance(minInsurance);

            setShowNoInsuranceModal(false);
          },
        }}
        onDismiss={() => {
          setShowNoInsuranceModal(false);
        }}
      />

      <DeliveryFeeModal
        visible={showHowDeliveryFeesModal}
        onClose={() => {
          setShowHowDeliveryFeesModal(false);
        }}
      />

      <FinishProfileModal
        visible={showFinishProfileModal}
        onClose={() => setShowFinishProfileModal(false)}
        onDone={() => setShowFinishProfileModal(false)}
      />
    </View>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background_1,
  },
  scrollViewContainer: {
    paddingLeft: 16,
    paddingRight: 16,
    height: {
      xs: '85%',
      md: '100%',
    },
    width: '100%',
  },
  scrollViewChild: {
    gap: 32,
    width: {
      xs: '100%',
      md: 1000,
    },
    flexGrow: 1,
  },
  overviewContainer: {
    flexGrow: 1,
    backgroundColor: theme.colors.background_2,
    borderTopWidth: 1,
    borderTopColor: theme.colors.card.default.borderColor,
  },
}));
